import { queryRecordsOutcome, getRecordOutcome, type ApiRecord, type FetchImpl } from './content-source.js';
import { createRecord, loadObjectPermissions } from './record-write.js';

/**
 * CAOS-366 — the workspace surface for tags: the labels on a record, attaching one, and finding
 * the other records that carry it.
 *
 * NO NEW API VERB. Everything here is ordinary `data query`, `data get` and `data create` against
 * the two standard objects the kernel ships (`tag`, `tag_assignment`) — which is the whole reason
 * the tagged record is named by ordinary `record_object`/`record_id` fields rather than attached
 * through the system `parent_id` the way `file` and the activity objects are. `parent_id` is never
 * serialized onto a record and `data query` deliberately refuses to narrow by it, so a tag attached
 * that way could be written and never read back by label. This module is what that decision buys:
 * it is written entirely in the public Data API, with no privileged path of its own.
 *
 * Everything is fail-soft, like the rest of the content sources. A person who cannot read `tag` —
 * an org that has not granted it — gets a record page with no tag strip, never a broken one.
 */

/** The two standard objects this module reads and writes. Bare keys, as the Data API takes them. */
const TAG_OBJECT = 'tag';
const TAG_ASSIGNMENT_OBJECT = 'tag_assignment';

/**
 * How many distinct labels an org's list is read in one page.
 *
 * The label set is read WHOLE and indexed by id, rather than asked for by the ids on this record,
 * because the Data API's filter takes `eq`/`contains`/`within` and has no `in` — so narrowing to a
 * handful of ids would mean one request per label. A tag vocabulary is small by construction (one
 * row per distinct label in the org, which is the entire reason `tag` is its own object), so one
 * page is the cheaper and simpler read. If an org ever outgrows this, the fix is an `in` operator
 * on the Data API, not a second read path here.
 */
const LABEL_PAGE = 200;

/** How many tagged records the "also tagged" answer resolves names for. */
const TAGGED_RECORD_LIMIT = 25;

/** One label on one record, with the assignment row that put it there. */
export interface RecordTag {
  readonly id: string;
  readonly name: string;
  /** The `tag_assignment` row — what a future "remove this label" would delete. */
  readonly assignmentId: string;
}

/**
 * The outcome of reading a record's labels. An `error` is told apart from an empty list on purpose:
 * a record with no tags should say so plainly, and a read that failed must not claim the same
 * thing — that is the same distinction {@link import('./content-source.js').QueryOutcome} draws.
 */
export type ResolvedTags =
  | {
      readonly status: 'ok';
      readonly tags: readonly RecordTag[];
      /**
       * Whether THIS person may attach a label here. Asked of the Engine about `tag_assignment`,
       * which is the object a new assignment is created on — deliberately NOT inferred from the
       * permissions on the record being looked at. Somebody who may edit an account has said
       * nothing about whether they may tag things, and CAOS-360's rule is that a control is
       * offered only where the answer actually says so.
       */
      readonly canAttach: boolean;
    }
  | { readonly status: 'error' };

/** A record carrying some label, as the "what else is tagged this?" answer names it. */
export interface TaggedRecord {
  readonly object: string;
  readonly recordId: string;
  /** The record's own display name, when it could be read; empty when it could not. */
  readonly name: string;
}

/** Sort labels the way a person reads them: alphabetically, case-insensitively. */
function byName(a: RecordTag, b: RecordTag): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

/** The best display name a record offers, without knowing which object it belongs to. */
function displayNameOf(record: ApiRecord): string {
  for (const key of ['name', 'title', 'subject', 'label']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

/**
 * The labels on one record.
 *
 * Two reads, in parallel: this record's assignments, and the org's label vocabulary. They are
 * joined here rather than on the server because there is no join verb and inventing one was
 * explicitly out of scope — see the module doc.
 *
 * An assignment whose label cannot be resolved is DROPPED rather than rendered nameless: the tag
 * row may have been deleted between the two reads, or the person may hold `tag_assignment` access
 * without `tag` access, and a chip with no word on it tells them nothing either way.
 */
export async function loadRecordTags(
  objectKey: string,
  recordId: string,
  fetchImpl: FetchImpl = fetch,
): Promise<ResolvedTags> {
  try {
    const [assignments, labels, attachPerms] = await Promise.all([
      queryRecordsOutcome(TAG_ASSIGNMENT_OBJECT, fetchImpl, LABEL_PAGE, {
        filters: [
          { field: 'record_object', op: 'eq', value: objectKey },
          { field: 'record_id', op: 'eq', value: recordId },
        ],
      }),
      queryRecordsOutcome(TAG_OBJECT, fetchImpl, LABEL_PAGE),
      // Read with them, not after: whether the control is offered is part of this answer, and a
      // strip that renders its chips and then grows a button reads as a page still making up its
      // mind. On any failure this is NO_WRITE, so the control is not offered.
      loadObjectPermissions(TAG_ASSIGNMENT_OBJECT, fetchImpl),
    ]);
    if (assignments.status !== 'ok' || labels.status !== 'ok') return { status: 'error' };

    const nameById = new Map<string, string>();
    for (const label of labels.records) {
      const name = typeof label['name'] === 'string' ? label['name'].trim() : '';
      if (name) nameById.set(String(label.id), name);
    }

    const seen = new Set<string>();
    const tags: RecordTag[] = [];
    for (const assignment of assignments.records) {
      const tagId = String(assignment['tag_id'] ?? '');
      const name = nameById.get(tagId);
      // The same label attached twice is one chip: the duplicate is a data accident, and showing
      // it twice would read as two different things.
      if (!tagId || !name || seen.has(tagId)) continue;
      seen.add(tagId);
      tags.push({ id: tagId, name, assignmentId: String(assignment.id) });
    }
    return { status: 'ok', tags: tags.sort(byName), canAttach: attachPerms.canCreate };
  } catch {
    return { status: 'error' };
  }
}

/**
 * The label with this name in this org, creating it only if there is not one already.
 *
 * FIND FIRST, and case-insensitively. `tag` is one row per distinct label precisely so that
 * "shutdown" is one thing across the org; letting a second row appear because somebody typed
 * "Shutdown" would split the group in half and neither half would be wrong-looking. The existing
 * row's spelling wins — the person who first named it chose it.
 *
 * There is no uniqueness constraint on the name behind this, so two people naming the same new
 * label at the same instant can still produce two rows. That is a real and deliberately accepted
 * race: the cost is a duplicate label somebody can merge, and the alternative is a unique index
 * that would need its own migration and its own decision about case.
 */
export async function findOrCreateTag(
  name: string,
  fetchImpl: FetchImpl = fetch,
): Promise<{ readonly id: string; readonly name: string } | null> {
  const wanted = name.trim();
  if (!wanted) return null;
  try {
    const existing = await queryRecordsOutcome(TAG_OBJECT, fetchImpl, LABEL_PAGE);
    if (existing.status === 'ok') {
      const match = existing.records.find(
        (r) => typeof r['name'] === 'string' && r['name'].trim().toLowerCase() === wanted.toLowerCase(),
      );
      if (match) return { id: String(match.id), name: String(match['name']).trim() };
    }
    const created = await createRecord(TAG_OBJECT, { name: wanted }, fetchImpl);
    if (created.status !== 'ok') return null;
    return { id: String(created.record.id), name: wanted };
  } catch {
    return null;
  }
}

/** Attach a label to a record. Returns whether the assignment was written. */
export async function attachTag(
  tagId: string,
  objectKey: string,
  recordId: string,
  fetchImpl: FetchImpl = fetch,
): Promise<boolean> {
  try {
    const created = await createRecord(
      TAG_ASSIGNMENT_OBJECT,
      { tag_id: tagId, record_object: objectKey, record_id: recordId },
      fetchImpl,
    );
    return created.status === 'ok';
  } catch {
    return false;
  }
}

/**
 * What else carries this label — the reverse question, and the half of the ticket a chip on its own
 * would not deliver.
 *
 * The assignments come back in ONE read (this is what the `tag_assignment_by_tag` index exists
 * for), and then each named record is read for its display name. That second step is one `data get`
 * per record, capped: without an `in` filter there is no cheaper exact way, and a page of links
 * that say "account · 3f9c…" would be a list nobody can use. Records beyond the cap are still
 * returned — with an empty name — so the count a caller renders is never quietly short.
 *
 * A record the caller may not read comes back with an empty name rather than being dropped: it is
 * still true that something is tagged, and the record-access gate on `data get` is what decides
 * whether they get to see which one.
 */
export async function loadTaggedRecords(
  tagId: string,
  excludeRecordId: string,
  fetchImpl: FetchImpl = fetch,
): Promise<readonly TaggedRecord[]> {
  try {
    const assignments = await queryRecordsOutcome(TAG_ASSIGNMENT_OBJECT, fetchImpl, LABEL_PAGE, {
      filters: [{ field: 'tag_id', op: 'eq', value: tagId }],
    });
    if (assignments.status !== 'ok') return [];

    const seen = new Set<string>();
    const targets: { object: string; recordId: string }[] = [];
    for (const assignment of assignments.records) {
      const object = String(assignment['record_object'] ?? '');
      const recordId = String(assignment['record_id'] ?? '');
      const key = `${object}:${recordId}`;
      // The record being looked at is excluded — it is already on screen, and listing it under
      // "also tagged" would read as a second, different record.
      if (!object || !recordId || recordId === excludeRecordId || seen.has(key)) continue;
      seen.add(key);
      targets.push({ object, recordId });
    }

    const named = await Promise.all(
      targets.slice(0, TAGGED_RECORD_LIMIT).map(async (t): Promise<TaggedRecord> => {
        const outcome = await getRecordOutcome(t.object, t.recordId, fetchImpl);
        const name = outcome.status === 'ok' && outcome.record ? displayNameOf(outcome.record) : '';
        return { object: t.object, recordId: t.recordId, name };
      }),
    );
    const rest = targets.slice(TAGGED_RECORD_LIMIT).map((t): TaggedRecord => ({ ...t, name: '' }));
    return [...named, ...rest];
  } catch {
    return [];
  }
}
